import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import api from "@/lib/api";
import { formatIDR, formatIDRShort, formatDate } from "@/lib/format";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const today = () => new Date().toISOString().slice(0, 10);

const FP_PERIODS = [
  { id: "today", label: "Hari" },
  { id: "week", label: "Minggu" },
  { id: "month", label: "Bulan" },
  { id: "year", label: "Tahun" },
  { id: "all", label: "Semua" },
  { id: "custom", label: "Periode" },
];

const Row = ({ label, value, strong, accent, indent }) => (
  <div className={`flex items-center justify-between py-2.5 ${strong ? "" : "border-b border-border last:border-0"}`}>
    <span className={`${indent ? "pl-4 text-sm text-muted-foreground" : "text-sm font-medium"} ${strong ? "font-heading font-bold text-base" : ""}`}>{label}</span>
    <span className={`font-semibold ${strong ? "font-heading text-lg" : "text-sm"} ${accent || ""}`}>{value}</span>
  </div>
);

export default function Reports() {
  const [period, setPeriod, range, setRange] = usePersistentPeriod("reports");
  const [pl, setPl] = useState(null);
  const [cashback, setCashback] = useState(null);
  const [fp, setFp] = useState(null);
  const [fpDetail, setFpDetail] = useState(null);
  const [fpPeriod, setFpPeriod, fpRange, setFpRange] = usePersistentPeriod("reports-fp");
  const [krPeriod, setKrPeriod, krRange, setKrRange] = usePersistentPeriod("reports-kr");
  const [kr, setKr] = useState(null);
  const [dep, setDep] = useState(null);
  const [depDetail, setDepDetail] = useState(null);

  const qstr = () => (period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`);
  const fpQstr = () => (fpPeriod === "custom" ? `period=custom&start=${fpRange.start}&end=${fpRange.end}` : `period=${fpPeriod}`);
  const krQstr = () => (krPeriod === "custom" ? `period=custom&start=${krRange.start}&end=${krRange.end}` : `period=${krPeriod}`);

  useEffect(() => { api.get(`/reports/profit-loss?period=${period}`).then((r) => setPl(r.data)); }, [period]);
  useEffect(() => { api.get(`/reports/cashback?period=${period}`).then((r) => setCashback(r.data)); }, [period]);
  useEffect(() => { api.get(`/reports/deposits?${qstr()}`).then((r) => setDep(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [period, range.start, range.end]);
  useEffect(() => { api.get(`/reports/financial-position?${fpQstr()}`).then((r) => setFp(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [fpPeriod, fpRange.start, fpRange.end]);
  useEffect(() => { api.get(`/reports/profit-loss?${krQstr()}`).then((r) => setKr(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [krPeriod, krRange.start, krRange.end]);
  if (!pl) return <PageSkeleton stats={2} rows={4} chart />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Laporan Laba Rugi</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Rincian laba kotor dan laba bersih bisnis Anda.</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <Card className="p-4 lg:col-span-3">
          <h3 className="font-heading text-sm font-semibold mb-1.5">Rincian Perhitungan (P&L)</h3>
          <Row label="Total Pendapatan" value={formatIDR(pl.total_income)} accent="text-emerald-600" />
          <Row label="Cashback Pelanggan" value={"- " + formatIDR(pl.total_cashback || 0)} accent="text-rose-600" indent />
          <Row label="Pendapatan Bersih" value={formatIDR(pl.revenue_net ?? pl.total_income)} accent="" />
          <Row label="Harga Pokok Penjualan (HPP)" value={"- " + formatIDR(pl.cogs)} accent="text-rose-600" indent />
          <div className="my-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 px-3">
            <Row label="Laba Kotor" value={formatIDR(pl.gross_profit)} strong accent="text-indigo-600 dark:text-indigo-400" />
          </div>
          <Row label="Biaya Operasional" value={"- " + formatIDR(pl.operating_expense)} accent="text-rose-600" indent />
          <div className="mt-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 px-3">
            <Row label="Laba Bersih" value={formatIDR(pl.net_profit)} strong accent="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded-xl border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">Margin Laba Kotor</p>
              <p className="font-heading text-xl font-extrabold mt-1">{pl.gross_margin.toFixed(1)}%</p>
            </div>
            <div className="rounded-xl border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">Margin Laba Bersih</p>
              <p className="font-heading text-xl font-extrabold mt-1">{pl.net_margin.toFixed(1)}%</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 lg:col-span-2 border border-[#C9A227]/30 relative overflow-hidden" data-testid="kroscek-card">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#C9A227]/10 blur-2xl" />
          <h3 className="font-heading text-sm font-semibold mb-1.5 relative">KROSCEK</h3>
          <p className="text-[11px] text-muted-foreground mb-2 relative">Laba Kotor − Total Pengeluaran = Hasil</p>
          <div className="relative mb-2 flex flex-wrap items-center gap-1" data-testid="kroscek-period-filter">
            {FP_PERIODS.map((p) => (
              <button key={p.id} type="button" data-testid={`kroscek-period-${p.id}`} onClick={() => setKrPeriod(p.id)}
                className={`h-7 px-2.5 rounded-lg text-[11px] font-medium transition-colors ${krPeriod === p.id ? "bg-[#0A1633] text-[#E8C766] shadow-sm" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
                {p.label}
              </button>
            ))}
            {krPeriod === "custom" && (
              <span className="flex items-center gap-1.5 ml-1">
                <input type="date" data-testid="kroscek-range-start" value={krRange.start} onChange={(e) => setKrRange({ ...krRange, start: e.target.value })}
                  className="h-7 rounded-lg bg-secondary border border-border text-foreground text-[11px] px-2" />
                <span className="text-muted-foreground text-[11px]">s/d</span>
                <input type="date" data-testid="kroscek-range-end" value={krRange.end} onChange={(e) => setKrRange({ ...krRange, end: e.target.value })}
                  className="h-7 rounded-lg bg-secondary border border-border text-foreground text-[11px] px-2" />
              </span>
            )}
          </div>
          <div className="relative">
            <Row label="Laba Kotor" value={formatIDR((kr || pl).gross_profit)} accent="text-indigo-600 dark:text-indigo-400" />
            <Row label="Total Pengeluaran" value={"- " + formatIDR((kr || pl).total_expense)} accent="text-rose-600" indent />
            <div className="mt-1 rounded-lg bg-[#0A1633] px-3" >
              <Row label="Hasil" value={formatIDR(((kr || pl).gross_profit) - ((kr || pl).total_expense))} strong
                accent={(((kr || pl).gross_profit) - ((kr || pl).total_expense)) >= 0 ? "text-[#E8C766]" : "text-rose-400"} />
            </div>
          </div>
        </Card>
      </div>

      {fp && (
        <Card className="p-4 sm:p-5 text-white relative overflow-hidden border border-[#C9A227]/30" style={{ backgroundImage: "linear-gradient(135deg, #0A1633 0%, #12306b 60%, #1e3fae 100%)" }} data-testid="financial-position">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[#C9A227]/15 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-heading text-sm font-semibold text-[#E8C766]">Total Posisi Keuangan</h3>
              <p className="text-[11px] text-blue-200 mt-0.5">Kas & Bank + Tagihan Pelanggan + Kasbon Belum Lunas − Titipan Pelanggan (Saldo) − Utang Supplier − Cashback Belum Dicairkan</p>
            </div>
            <p className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight shrink-0" data-testid="fp-result">{formatIDR(fp.result)}</p>
          </div>
          <div className="relative mt-3 flex flex-wrap items-center gap-1" data-testid="fp-period-filter">
            {FP_PERIODS.map((p) => (
              <button key={p.id} type="button" data-testid={`fp-period-${p.id}`} onClick={() => setFpPeriod(p.id)}
                className={`h-7 px-2.5 rounded-lg text-[11px] font-medium transition-colors ${fpPeriod === p.id ? "bg-[#C9A227] text-[#0A1633] shadow-sm" : "bg-white/10 text-blue-100 hover:bg-white/20"}`}>
                {p.label}
              </button>
            ))}
            {fpPeriod === "custom" && (
              <span className="flex items-center gap-1.5 ml-1">
                <input type="date" data-testid="fp-range-start" value={fpRange.start} onChange={(e) => setFpRange({ ...fpRange, start: e.target.value })}
                  className="h-7 rounded-lg bg-white/10 border border-white/20 text-white text-[11px] px-2 [color-scheme:dark]" />
                <span className="text-blue-200 text-[11px]">s/d</span>
                <input type="date" data-testid="fp-range-end" value={fpRange.end} onChange={(e) => setFpRange({ ...fpRange, end: e.target.value })}
                  className="h-7 rounded-lg bg-white/10 border border-white/20 text-white text-[11px] px-2 [color-scheme:dark]" />
              </span>
            )}
          </div>
          <div className="relative mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fp.components.map((c) => (
              <button key={c.id} data-testid={`fp-comp-${c.id}`} onClick={() => setFpDetail(c)}
                className="text-left rounded-xl bg-white/5 border border-white/10 hover:border-[#C9A227]/60 hover:bg-white/10 transition-colors px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-blue-200 leading-tight">{c.label}</p>
                <p className={`font-heading text-base font-bold mt-0.5 ${c.sign < 0 ? "text-rose-300" : "text-emerald-300"}`}>{c.sign < 0 ? "− " : "+ "}{formatIDR(c.total)}</p>
                <p className="text-[10px] text-blue-200/70 mt-0.5">{c.details.length} data · klik untuk rincian</p>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={!!fpDetail} onOpenChange={(o) => !o && setFpDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Rincian: {fpDetail?.label}</DialogTitle>
            <DialogDescription className="text-xs">Total {fpDetail ? formatIDR(fpDetail.total) : ""} dari {fpDetail?.details.length || 0} data.</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto" data-testid="fp-detail-panel">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Tanggal</TableHead>
                  <TableHead className="text-xs">Nama</TableHead>
                  <TableHead className="text-xs">Referensi</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  <TableHead className="text-xs text-right">Dibayar</TableHead>
                  <TableHead className="text-xs text-right">Sisa</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(fpDetail?.details || []).length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground text-xs py-6">Tidak ada data.</TableCell></TableRow>
                )}
                {(fpDetail?.details || []).map((d, i) => (
                  <TableRow key={i} data-testid={`fp-detail-row-${i}`}>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground">{d.date ? formatDate(d.date) : "-"}</TableCell>
                    <TableCell className="text-xs font-medium">{d.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{d.ref || "-"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{d.status || "-"}</TableCell>
                    <TableCell className="text-xs text-right whitespace-nowrap">{formatIDR(d.total)}</TableCell>
                    <TableCell className="text-xs text-right whitespace-nowrap text-emerald-600">{formatIDR(d.paid)}</TableCell>
                    <TableCell className="text-xs text-right whitespace-nowrap font-semibold text-rose-600">{formatIDR(d.remaining)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFpDetail(null)} data-testid="fp-detail-back">Kembali</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {cashback && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2 gap-2">
            <h3 className="font-heading text-sm font-semibold">Laporan Cashback</h3>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Cashback</p>
              <p className="font-heading text-lg font-extrabold text-rose-600" data-testid="cashback-total">{formatIDR(cashback.total_cashback)}</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">Cashback mengurangi pendapatan & laba sebesar {formatIDR(cashback.total_cashback)} pada periode ini.</p>
          {cashback.items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">Belum ada cashback pada periode ini.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Kategori</TableHead><TableHead>Pelanggan</TableHead><TableHead className="text-right">Harga Jual</TableHead><TableHead className="text-right">Cashback</TableHead></TableRow></TableHeader>
                <TableBody>
                  {cashback.items.map((c) => (
                    <TableRow key={c.id} data-testid={`cashback-row-${c.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{formatDate(c.date)}</TableCell>
                      <TableCell className="text-xs">{c.category}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.customer_name}</TableCell>
                      <TableCell className="text-right text-xs whitespace-nowrap">{formatIDR(c.amount)}</TableCell>
                      <TableCell className="text-right text-xs font-semibold text-rose-600 whitespace-nowrap">{formatIDR(c.cashback)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      {dep && (
        <Card className="p-4" data-testid="deposits-report">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5">Laporan Titipan Pelanggan</h3>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Saldo Titipan Aktif</p>
              <p className="font-heading text-lg font-extrabold text-emerald-600" data-testid="dep-report-active">{formatIDR(dep.saldo_aktif)}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {[
              { id: "masuk", label: "Titipan Masuk", total: dep.masuk_total, rows: dep.masuk, accent: "text-emerald-600", note: "dari pembatalan order" },
              { id: "used", label: "Titipan Terpakai", total: dep.used_total, rows: dep.used, accent: "text-sky-600", note: "dipakai untuk order lain" },
              { id: "refund", label: "Titipan Direfund", total: dep.refund_total, rows: dep.refund, accent: "text-rose-600", note: "dikembalikan ke pelanggan" },
            ].map((c) => (
              <button key={c.id} data-testid={`dep-report-${c.id}`} onClick={() => setDepDetail(c)}
                className="text-left rounded-xl border border-border hover:border-[#C9A227]/60 transition-colors px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">{c.label}</p>
                <p className={`font-heading text-base font-bold mt-0.5 ${c.accent}`}>{formatIDR(c.total)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{c.rows.length} transaksi · {c.note}</p>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Titipan masuk/terpakai/direfund dihitung sesuai periode. Saldo aktif adalah total saldo titipan pelanggan saat ini.</p>
        </Card>
      )}

      <Dialog open={!!depDetail} onOpenChange={(o) => !o && setDepDetail(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Rincian: {depDetail?.label}</DialogTitle>
            <DialogDescription className="text-xs">Total {depDetail ? formatIDR(depDetail.total) : ""} dari {depDetail?.rows.length || 0} transaksi.</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto" data-testid="dep-report-detail">
            <Table>
              <TableHeader><TableRow><TableHead className="text-xs">Tanggal</TableHead><TableHead className="text-xs">Pelanggan</TableHead><TableHead className="text-xs">Referensi</TableHead><TableHead className="text-xs text-right">Nominal</TableHead></TableRow></TableHeader>
              <TableBody>
                {(depDetail?.rows || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground text-xs py-6">Tidak ada data.</TableCell></TableRow>}
                {(depDetail?.rows || []).map((d, i) => (
                  <TableRow key={i} data-testid={`dep-report-row-${i}`}>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground">{d.date ? formatDate(d.date) : "-"}</TableCell>
                    <TableCell className="text-xs font-medium">{d.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{d.ref || "-"}</TableCell>
                    <TableCell className="text-xs text-right whitespace-nowrap font-semibold">{formatIDR(d.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepDetail(null)} data-testid="dep-report-back">Kembali</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4">
          <h3 className="font-heading text-sm font-semibold mb-1.5">Pendapatan per Kategori</h3>
          {pl.income_by_category.length === 0 && <p className="text-xs text-muted-foreground py-4">Tidak ada data.</p>}
          {pl.income_by_category.map((c) => <Row key={c.category} label={c.category} value={formatIDR(c.amount)} accent="text-emerald-600" />)}
        </Card>
        <Card className="p-4">
          <h3 className="font-heading text-sm font-semibold mb-1.5">Pengeluaran per Kategori</h3>
          {pl.expense_by_category.length === 0 && <p className="text-xs text-muted-foreground py-4">Tidak ada data.</p>}
          {pl.expense_by_category.map((c) => <Row key={c.category} label={c.category} value={formatIDR(c.amount)} accent="text-rose-600" />)}
        </Card>
      </div>
    </div>
  );
}
